import React from 'react';
import {
  Archive,
  CheckCircle,
  FilePlus, HelpCircle,
  Repeat,
  Send, Slack,
  UserCheck, UserPlus,
} from 'react-feather';
import SendEmailHandler from '../views/shared/TaskHandlerModal/TriggeredAutomationModal/SendEmailHandler';
import ScheduleDripEmailHandler from '../views/shared/TaskHandlerModal/TriggeredAutomationModal/ScheduleDripEmailHandler';
import AssignTaskHandler from '../views/shared/TaskHandlerModal/TriggeredAutomationModal/AssignTaskHandler';
import {
  EventDetail,
  EventType,
  ActionType,
  ModalSize, TriggerType
} from '../types';
import { EventAttributes } from './types';
import Label from '../components/Label';
import TaskTemplateForm from '../views/AdminExperience/Workflows/ActionBuilder/TaskTemplateForm';
import ConditionForm from '../views/AdminExperience/Workflows/ActionBuilder/ConditionForm';
import AutoAssignmentPolicyForm from '../views/AdminExperience/Workflows/ActionBuilder/AutoAssignmentPolicyForm';
import EmailBuilder from '../views/AdminExperience/Workflows/ActionBuilder/EmailBuilder';
import FormBuilder from '../views/AdminExperience/Workflows/TriggerBuilder/FormBuilder';
import HttpRequestForm from '../views/AdminExperience/Workflows/ActionBuilder/HttpRequestForm';
import AssignUserHandler from '../views/shared/TaskHandlerModal/TriggeredAutomationModal/AssignUserHandler';
import ArchiveEngagementHandler
  from '../views/shared/TaskHandlerModal/TriggeredAutomationModal/ArchiveEngagementHandler';
import SlackMessageForm from '../views/AdminExperience/Workflows/ActionBuilder/SlackMessageForm';
import DocumentGeneratorForm from '../views/AdminExperience/Workflows/ActionBuilder/DocumentGeneratorForm';

export const SM = 'sm';
export const MD = 'md';
export const LG = 'lg';
export const XL = 'xl';

export const MODAL_SIZE_OPTIONS = [
  {
    label: 'Small',
    value: SM,
  },
  {
    label: 'Medium',
    value: MD,
  },
  {
    label: 'Large',
    value: LG,
  },
];

// ACTIONS
export const ASSIGN_TASK = 'ASSIGN_TASK';
export const ARCHIVE_ENGAGEMENT = 'ARCHIVE_ENGAGEMENT';
export const CHECK_CONDITION = 'CHECK_CONDITION';
export const SEND_EMAIL = 'SEND_EMAIL';
export const ASSIGN_USER = 'ASSIGN_USER';
export const HTTP_REQUEST = 'HTTP_REQUEST';
export const SCHEDULE_DRIP_CAMPAIGN = 'SCHEDULE_DRIP_CAMPAIGN';
export const GENERATE_DOCUMENT = 'GENERATE_DOCUMENT';
export const POST_SLACK_MESSAGE = 'POST_SLACK_MESSAGE';
export const ACTION_TYPES: ActionType[] = [
  ASSIGN_TASK,
  ARCHIVE_ENGAGEMENT,
  CHECK_CONDITION,
  SEND_EMAIL,
  ASSIGN_USER,
  HTTP_REQUEST,
  SCHEDULE_DRIP_CAMPAIGN,
  POST_SLACK_MESSAGE,
  GENERATE_DOCUMENT,
];

// TRIGGERS
export const FORM_SUBMITTED = 'FORM_SUBMITTED';
export const TASK_COMPLETED = 'TASK_COMPLETED';
export const TRIGGER_TYPES: TriggerType[] = [
  FORM_SUBMITTED,
  TASK_COMPLETED,
];

export const EVENT_ATTRIBUTES_BY_EVENT_TYPE: Record<EventType, EventAttributes> = {
  [FORM_SUBMITTED]: {
    icon: 'file-text',
    eventType: FORM_SUBMITTED,
    stepTitle: 'Form submitted',
    eventTitle: 'Form submitted',
    stepDescription: 'Triggered when a form is submitted',
    getStatement: (event: EventDetail) => (
      // @ts-ignore
      <span>{event.initiatorAccount.firstName} {event.initiatorAccount.lastName} submitted the <Label>{event.completedStep.trigger.form.perspective.name}</Label> form.</span>
    ),
    stepBuilderComponent: FormBuilder,
    // only actions should have this field
    triggeredActionHandler: () => <div />,
  },
  [TASK_COMPLETED]: {
    icon: 'check-circle',
    eventType: TASK_COMPLETED,
    stepTitle: 'Task completed',
    eventTitle: 'Task completed',
    stepDescription: 'Triggered when a task is completed',
    getStatement: (event: EventDetail) => (
      // @ts-ignore
      <span>{event.initiatorAccount.firstName} {event.initiatorAccount.lastName} completed the <Label>{event.task ? event.task.title : 'MEOW'}</Label> task.</span>
    ),
    stepBuilderComponent: () => <div />,
    triggeredActionHandler: () => <div />,
  },
  [ASSIGN_USER]: {
    icon: 'user-plus',
    eventType: ASSIGN_USER,
    stepTitle: 'Assign user',
    eventTitle: 'Role assigned',
    stepDescription: 'Assign a user to a role',
    getStatement: (event: EventDetail) => (
      <span>{event.roleAssignment.account.firstName} {event.roleAssignment.account.lastName} was assigned to the <Label>{event.roleAssignment.role.name}</Label> role.</span>
    ),
    stepBuilderComponent: AutoAssignmentPolicyForm,
    triggeredActionHandler: AssignUserHandler,
    isApprovable: true,
  },
  [ASSIGN_TASK]: {
    icon: 'user-check',
    eventType: ASSIGN_TASK,
    stepTitle: 'Assign task',
    eventTitle: 'Task assigned',
    stepDescription: 'Assign a task to a role',
    getStatement: (event: EventDetail) => (
      // @ts-ignore - assign tasks will always be the result of a completed step
      <span>{event.task.assignedTo.firstName} {event.task.assignedTo.lastName} was assigned with the <Label>{event.task.title}</Label> task.</span>
    ),
    stepBuilderComponent: TaskTemplateForm,
    triggeredActionHandler: AssignTaskHandler,
    isApprovable: true,
  },
  [SEND_EMAIL]: {
    icon: 'send',
    eventType: SEND_EMAIL,
    stepTitle: 'Send email',
    eventTitle: 'Email sent',
    stepDescription: 'Send an email to a role',
    getStatement: (event: EventDetail) => (
      // @ts-ignore  TODO - I'd like to say who this was sent to here
      <span>The <Label>{event.completedStep.action?.emailTemplate.subject}</Label> email was sent.</span>
    ),
    stepBuilderComponent: EmailBuilder,
    triggeredActionHandler: SendEmailHandler,
    isApprovable: true,
  },
  [ARCHIVE_ENGAGEMENT]: {
    icon: 'archive',
    eventType: ARCHIVE_ENGAGEMENT,
    stepTitle: 'Archive engagement',
    eventTitle: 'Engagement archived',
    stepDescription: 'Archive the engagement',
    getStatement: () => 'The engagement was archived.',
    stepBuilderComponent: () => <div />,
    triggeredActionHandler: ArchiveEngagementHandler,
    isApprovable: true,
  },
  [CHECK_CONDITION]: {
    icon: 'help-circle',
    eventType: CHECK_CONDITION,
    stepTitle: 'Check condition',
    eventTitle: 'Condition passed',
    stepDescription: 'Check something before proceeding with the next steps',
    getStatement: () => 'A condition was checked.',
    stepBuilderComponent: ConditionForm,
    triggeredActionHandler: () => <div />,
  },
  [HTTP_REQUEST]: {
    icon: 'at-sign',
    eventType: HTTP_REQUEST,
    stepTitle: 'Make HTTP request',
    eventTitle: 'HTTP request sent',
    stepDescription: 'Send an HTTP request',
    getStatement: () => 'An HTTP request was sent.',
    stepBuilderComponent: HttpRequestForm,
    triggeredActionHandler: () => <div />,
  },
  [POST_SLACK_MESSAGE]: {
    icon: 'slack',
    eventType: POST_SLACK_MESSAGE,
    stepTitle: 'Post slack message',
    eventTitle: 'Slack message posted.',
    stepDescription: 'Send a slack message to a channel.',
    getStatement: () => 'A Slack message was posted.',
    stepBuilderComponent: SlackMessageForm,
    triggeredActionHandler: () => <div />,
  },
  [SCHEDULE_DRIP_CAMPAIGN]: {
    icon: 'repeat',
    eventType: SCHEDULE_DRIP_CAMPAIGN,
    stepTitle: 'Schedule email campaign',
    eventTitle: 'Email campaign scheduled',
    stepDescription: 'Email campaign scheduled',
    getStatement: () => 'A drip campaign was scheduled.',
    stepBuilderComponent: () => <div />,
    triggeredActionHandler: ScheduleDripEmailHandler,
  },
  [GENERATE_DOCUMENT]: {
    icon: 'file-plus',
    eventType: GENERATE_DOCUMENT,
    stepTitle: 'Generate document',
    eventTitle: 'Document generated',
    stepDescription: 'Generate a document from a template',
    getStatement: () => 'A document was generated.',
    stepBuilderComponent: DocumentGeneratorForm,
    triggeredActionHandler: () => <div />,
  },
};

export const UPLOAD_DOCUMENT = 'UPLOAD_DOCUMENT';
export const SIGN_DOCUMENT = 'SIGN_DOCUMENT';
export const OTHER = 'OTHER';

export const FEATHER_ICONS = [
  'activity',
  'airplay',
  'alert-circle',
  'alert-octagon',
  'alert-triangle',
  'align-center',
  'align-justify',
  'align-left',
  'align-right',
  'anchor',
  'aperture',
  'archive',
  'arrow-down-circle',
  'arrow-down-left',
  'arrow-down-right',
  'arrow-down',
  'arrow-left-circle',
  'arrow-left',
  'arrow-right-circle',
  'arrow-right',
  'arrow-up-circle',
  'arrow-up-left',
  'arrow-up-right',
  'arrow-up',
  'at-sign',
  'award',
  'bar-chart-2',
  'bar-chart',
  'battery-charging',
  'battery',
  'bell-off',
  'bell',
  'bluetooth',
  'bold',
  'book-open',
  'book',
  'bookmark',
  'box',
  'briefcase',
  'calendar',
  'camera-off',
  'camera',
  'cast',
  'check-circle',
  'check-square',
  'check',
  'chevron-down',
  'chevron-left',
  'chevron-right',
  'chevron-up',
  'chevrons-down',
  'chevrons-left',
  'chevrons-right',
  'chevrons-up',
  'chrome',
  'circle',
  'clipboard',
  'clock',
  'cloud-drizzle',
  'cloud-lightning',
  'cloud-off',
  'cloud-rain',
  'cloud-snow',
  'cloud',
  'code',
  'codepen',
  'codesandbox',
  'coffee',
  'columns',
  'command',
  'compass',
  'copy',
  'corner-down-left',
  'corner-down-right',
  'corner-left-down',
  'corner-left-up',
  'corner-right-down',
  'corner-right-up',
  'corner-up-left',
  'corner-up-right',
  'cpu',
  'credit-card',
  'crop',
  'crosshair',
  'database',
  'delete',
  'disc',
  'dollar-sign',
  'download-cloud',
  'download',
  'droplet',
  'edit-2',
  'edit-3',
  'edit',
  'external-link',
  'eye-off',
  'eye',
  'facebook',
  'fast-forward',
  'feather',
  'figma',
  'file-minus',
  'file-plus',
  'file-text',
  'file',
  'film',
  'filter',
  'flag',
  'folder-minus',
  'folder-plus',
  'folder',
  'framer',
  'frown',
  'gift',
  'git-branch',
  'git-commit',
  'git-merge',
  'git-pull-request',
  'github',
  'gitlab',
  'globe',
  'grid',
  'hard-drive',
  'hash',
  'headphones',
  'heart',
  'help-circle',
  'hexagon',
  'home',
  'image',
  'inbox',
  'info',
  'instagram',
  'italic',
  'key',
  'layers',
  'layout',
  'life-buoy',
  'link-2',
  'link',
  'linkedin',
  'list',
  'loader',
  'lock',
  'log-in',
  'log-out',
  'mail',
  'map-pin',
  'map',
  'maximize-2',
  'maximize',
  'meh',
  'menu',
  'message-circle',
  'message-square',
  'mic-off',
  'mic',
  'minimize-2',
  'minimize',
  'minus-circle',
  'minus-square',
  'minus',
  'monitor',
  'moon',
  'more-horizontal',
  'more-vertical',
  'mouse-pointer',
  'move',
  'music',
  'navigation-2',
  'navigation',
  'octagon',
  'package',
  'paperclip',
  'pause-circle',
  'pause',
  'pen-tool',
  'percent',
  'phone-call',
  'phone-forwarded',
  'phone-incoming',
  'phone-missed',
  'phone-off',
  'phone-outgoing',
  'phone',
  'pie-chart',
  'play-circle',
  'play',
  'plus-circle',
  'plus-square',
  'plus',
  'pocket',
  'power',
  'printer',
  'radio',
  'refresh-ccw',
  'refresh-cw',
  'repeat',
  'rewind',
  'rotate-ccw',
  'rotate-cw',
  'rss',
  'save',
  'scissors',
  'search',
  'send',
  'server',
  'settings',
  'share-2',
  'share',
  'shield-off',
  'shield',
  'shopping-bag',
  'shopping-cart',
  'shuffle',
  'sidebar',
  'skip-back',
  'skip-forward',
  'slack',
  'slash',
  'sliders',
  'smartphone',
  'smile',
  'speaker',
  'square',
  'star',
  'stop-circle',
  'sun',
  'sunrise',
  'sunset',
  'tablet',
  'tag',
  'target',
  'terminal',
  'thermometer',
  'thumbs-down',
  'thumbs-up',
  'toggle-left',
  'toggle-right',
  'tool',
  'trash-2',
  'trash',
  'trello',
  'trending-down',
  'trending-up',
  'triangle',
  'truck',
  'tv',
  'twitch',
  'twitter',
  'type',
  'umbrella',
  'underline',
  'unlock',
  'upload-cloud',
  'upload',
  'user-check',
  'user-minus',
  'user-plus',
  'user-x',
  'user',
  'users',
  'video-off',
  'video',
  'voicemail',
  'volume-1',
  'volume-2',
  'volume-x',
  'volume',
  'watch',
  'wifi-off',
  'wifi',
  'wind',
  'x-circle',
  'x-octagon',
  'x-square',
  'x',
  'youtube',
  'zap-off',
  'zap',
  'zoom-in',
  'zoom-out',
];
